import { Options } from './groups'

type QueryParams = Record<
  string,
  string | number | boolean | null | undefined | (string | number)[]
>
type RequestBody = Record<string, any>

export type FilterT<k, v> = {
  label?: string
  key: k

  // server serialization
  toQueryParams?: (value: v) => QueryParams
  toRequestBody?: (value: v) => RequestBody
}

export type FilterSingle<v, k> = {
  type: 'single'
  value: v | null
  options: Options<v>
} & FilterT<k, v>

export type FilterSingleWithAll<v, k> = {
  type: 'singleWithAll'
  options: Options<(v | 'All') | v>
  value: (v | 'All') | v | null
} & FilterT<k, v>

export type SearchStr<v, k> = {
  type: 'searchStr'
  options: Options<v>
  value: v | null
} & FilterT<k, v>

export type FilterMultiple<v, k> = {
  type: 'multiple'
  options: Options<v>
  value: v[] | null
} & FilterT<k, v>

export type FilterRange<k> = {
  label: [string, string]
  options: Options<number>
  type: 'range'
  value: [number, number] | null
} & Omit<FilterT<k, [number, number]>, 'label'>

export type FilterDateRange<k> = {
  label: [string, string]
  options: Options<number>
  type: 'dateRange'
  value: [Date, Date] | null
} & Omit<FilterT<k, [Date, Date]>, 'label'>

export type FilterIncludes<k> = {
  type: 'includes'
  options: Options<number>
  value: number | string | null
} & FilterT<k, number | string>

export type FilterMultipleIncludes<k> = {
  type: 'multipleIncludes'
  options: Options<number>
  value: (number | string)[] | null
} & FilterT<k, (number | string)[]>

export type FilterCheckbox<k> = {
  type: 'checkbox'
  options: Options<boolean>
  value: boolean | null
} & FilterT<k, boolean>

export type SearchStrFilter = {
  options: Options<string>
  value: string
}

export type FilterTypes<values, k, d = string> =
  | FilterMultiple<values, k>
  | FilterSingle<values, k>
  | FilterSingleWithAll<values, k>
  | SearchStr<values, k>
  | FilterRange<k>
  | FilterDateRange<k>
  | FilterIncludes<k>
  | FilterMultipleIncludes<k>
  | FilterCheckbox<k>

export type CustomFilterState<F extends { [key: string]: { value: any } }> =
  Partial<{
    [k in keyof F]: Partial<FilterTypes<F[k]['value'], k, keyof F>>
  }>

export function getFilterState<
  DefaultFilters extends Record<string, FilterTypes<any, any, any>>,
  CustomFilters extends CustomFilterState<DefaultFilters>
>(customFilterState: CustomFilters, defaultFilterState: DefaultFilters) {
  // Add custom filters to default filters //

  const combinedState = {} as any

  for (const key in defaultFilterState) {
    const defaultFilter = defaultFilterState[key]
    const customFilter = customFilterState[key]

    const combinedFilter = {
      key: defaultFilter.key,
      type: customFilter?.type || defaultFilter.type,
      value: customFilter?.value ?? defaultFilter.value,
      options: customFilter?.options
        ? [...customFilter.options]
        : [...defaultFilter.options],
      label: customFilter?.label || defaultFilter.label,
      toQueryParams: customFilter?.toQueryParams || defaultFilter.toQueryParams,
      toRequestBody: customFilter?.toRequestBody || defaultFilter.toRequestBody
    } as FilterTypes<any, any, any>

    if (combinedFilter.type === 'singleWithAll') {
      combinedFilter.options.unshift({ name: 'All', value: 'All' })
    }

    combinedState[key] = combinedFilter
  }

  return combinedState as DefaultFilters
}

// Check if filter is server side //
// i.e., has queryParams or bodyParams defined //
export function isFilterServerSide<F extends FilterTypes<any, any, any>>(
  filter: F
) {
  return filter.toQueryParams || filter.toRequestBody
}

export function buildParams(filters: Record<string, any>) {
  if (!filters) {
    return {
      queryParams: {},
      bodyParams: {}
    }
  }

  const query: Record<string, any> = {}
  const body: Record<string, any> = {}

  for (const f of Object.values(filters)) {
    const v = f.value
    if (v == null) continue

    if (f.toQueryParams) Object.assign(query, f.toQueryParams(v))
    if (f.toRequestBody) Object.assign(body, f.toRequestBody(v))
  }

  // optional: drop null/undefined and stabilize key order
  const cleanedQuery = Object.fromEntries(
    Object.entries(query)
      .filter(([, val]) => val !== undefined && val !== null && val !== '')
      .sort(([a], [b]) => a.localeCompare(b))
  )

  const cleanedBody = Object.fromEntries(
    Object.entries(body).filter(([, val]) => val !== undefined || val === null)
  )

  return { queryParams: cleanedQuery, bodyParams: cleanedBody }
}

export function runCheck<F extends FilterTypes<any, any, any>>(
  filter: F,
  itemValue: any
) {
  if (
    filter.value === null ||
    filter.value === '' ||
    filter.value === 'All' ||
    isFilterServerSide(filter)
  )
    return true

  if (filter.type === 'checkbox') {
    return filter.value ? Boolean(itemValue) : true
  }

  if (
    filter.type === 'searchStr' &&
    typeof itemValue === 'string' &&
    typeof filter.value === 'string'
  ) {
    const lowerCase = itemValue.toLowerCase()
    return lowerCase.includes(filter.value.toLowerCase())
  }

  if (filter.type === 'includes') {
    if (itemValue.includes(filter.value)) return true
    return
  }

  if (filter.type === 'multipleIncludes') {
    if (filter.value.length === 0) return true
    for (const val of filter.value) {
      if (itemValue.includes(val)) return true
    }
    return false
  }

  // TODO: ADD EXACT Number for player

  if (filter.type === 'single' || filter.type === 'singleWithAll') {
    if (filter.value === itemValue) return true
    if (
      typeof filter.value === 'string' &&
      typeof itemValue === 'boolean' &&
      filter.value === JSON.stringify(itemValue)
    )
      return true
    return false
  }

  if (filter.type === 'multiple') {
    if (filter.value.length === 0) return true
    if (filter.value.includes('All')) return true
    if (filter.value.includes(itemValue)) return true
    return false
  }

  if (filter.type === 'range' || filter.type === 'dateRange') {
    if (!filter.value) return true
    if (itemValue >= filter.value[0] && itemValue <= filter.value[1]) {
      return true
    }
    return false
  }

  return true
}

export const isFilterOptionsEmpty = (filter: FilterTypes<any, any, any>) => {
  if (filter.options && filter.options.length <= 1) {
    return true
  }
  return (
    filter.options &&
    filter.options.length <= 2 &&
    filter.options.some((option) => String(option.value) === 'All')
  )
}

// True when FilterInput would render an empty option-based control (nothing
// useful to pick). Callers use this to hide such filters at the call site.
export const isEmptyOptionFilter = (filter: FilterTypes<any, any, any>) =>
  (filter.type === 'multiple' ||
    filter.type === 'multipleIncludes' ||
    filter.type === 'includes' ||
    filter.type === 'single' ||
    filter.type === 'singleWithAll') &&
  isFilterOptionsEmpty(filter)
